import { Transition } from '@headlessui/react';
import { memo, useMemo, useRef, useSyncExternalStore } from 'react';
import VideoElementRemoveButton from './VideoElementRemoveButton';
import VideoElementVolumeControl from './VideoElementVolumeControl';
import VideoElementFill from './VideoElementFill';
import VideoElementReplace from './VideoElementReplace';
import { ElementTransitionButton } from '../common/ElementTransitionButton';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { VideoElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { getPlayerConfigElementById } from '@/player-config/src/utils/playerConfigUtils';

function VideoElementMenu({ videoElementId }: { videoElementId: string }) {
  const menuRef = useRef<HTMLDivElement>(null);
  const { clipData } = useClipsContext();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isUseCLReplaceVideoOverlaysEnabled = featureFlags[FeatureFlagKeys.Use_CL_Replace_Video_Overlays];

  const selectedElement = useMemo(() => {
    return getPlayerConfigElementById(clipData.asset_metadata.player_config, videoElementId) as VideoElement;
  }, [clipData.asset_metadata.player_config, videoElementId]);

  return (
    <Transition
      appear
      as="div"
      show
      className="z-[60] flex cursor-auto items-center justify-center transition-all duration-100"
      enterFrom="opacity-0 max-h-0"
      enterTo="opacity-100 max-h-96"
      leaveFrom="opacity-100 max-h-96"
      leaveTo="opacity-0 max-h-0"
    >
      <div
        id="video-element-menu"
        className="relative flex space-x-1 rounded-lg border bg-white p-0.5 shadow"
        onPointerDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        ref={menuRef}
      >
        {isUseCLReplaceVideoOverlaysEnabled && <VideoElementReplace selectedElement={selectedElement} />}
        <div className="relative z-10">
          <VideoElementFill selectedElement={selectedElement} />
        </div>
        <div className="relative z-10">
          <VideoElementVolumeControl selectedElement={selectedElement} />
        </div>

        <div className="relative z-10">
          <ElementTransitionButton selectedElement={selectedElement} />
        </div>

        <div className="relative z-10">
          <VideoElementRemoveButton videoElementId={videoElementId} />
        </div>
      </div>
    </Transition>
  );
}

export default memo(VideoElementMenu);
